import APIService from "./APIService";
import { getCookie } from "../utils/CookieUtil";
import {
  getRecipient,
  getMessageContent,
  getDefaultSubject,
} from "../utils/CommunicationUtil";

/**
 * API service for accessing simulation heuristics and recommendations
 */
const SimulationHeuristicsAPI = {
  /**
   * Get tailored simulation recommendations for all users or a specific user
   * @param {string|null} userUuid - Optional user UUID to filter recommendations for a specific user
   * @returns {Promise<Array>} List of simulation recommendations
   */
  getTailoredUserSimulations: async (userUuid = null) => {
    let endpoint = "/api/v1/simulation/heuristics/user";

    // If a specific userUuid is provided, add it as a query parameter
    if (userUuid) {
      endpoint += `/${userUuid}`;
    }

    console.log(
      `[SimulationHeuristicsAPI][getTailoredUserSimulations] Calling API endpoint: ${endpoint}`,
      {
        userUuid,
      },
    );

    try {
      const response = await APIService.get(endpoint);
      console.log(
        `[SimulationHeuristicsAPI][getTailoredUserSimulations] API response successful from ${endpoint}`,
      );
      console.log(
        "[SimulationHeuristicsAPI][getTailoredUserSimulations] Response data:",
        response,
      );
      return response;
    } catch (error) {
      console.error(
        `[SimulationHeuristicsAPI][getTailoredUserSimulations] ERROR calling ${endpoint}:`,
        error,
      );
      console.error(
        "[SimulationHeuristicsAPI][getTailoredUserSimulations] Error details:",
        {
          message: error.message,
          stack: error.stack,
          response: error.response
            ? {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
              }
            : "No response object",
        },
      );
      throw error; // Re-throw for component handling
    }
  },

  /**
   * Get message content by UUID
   * @param {string} messageUuid - The UUID of the message to retrieve
   * @returns {Promise<Object>} The message content and metadata
   */
  getMessageById: async (messageUuid) => {
    console.log(
      `[SimulationHeuristicsAPI][getMessageById] Fetching message with UUID: ${messageUuid}`,
    );

    try {
      const response = await APIService.get(
        `/api/v1/simulation/messages/${messageUuid}`,
      );
      console.log(
        `[SimulationHeuristicsAPI][getMessageById] Successfully retrieved message: ${messageUuid}`,
      );
      return response;
    } catch (error) {
      console.error(
        `[SimulationHeuristicsAPI][getMessageById] ERROR fetching message ${messageUuid}:`,
        error,
      );
      throw error;
    }
  },

  sendEmailSimulation: async (communicationSimulationData) => {
    console.log(
      "[SimulationHeuristicsAPI][sendEmailSimulation] Sending communication simulation data:",
      communicationSimulationData,
    );

    try {
      // Make the API call to send the communication simulation
      const response = await APIService.post(
        "/api/v1/simulation/communication-simulations",
        communicationSimulationData,
      );

      console.log(
        "[SimulationHeuristicsAPI][sendEmailSimulation] Communication simulation sent successfully:",
        response,
      );

      return response;
    } catch (error) {
      console.error(
        "[SimulationHeuristicsAPI][sendEmailSimulation] Error sending communication simulation:",
        error,
      );
      throw error;
    }
  },

  /**
   * Service for handling RecentSimulations API operations
   *
   * Expected RecentActivityDTO structure (based on backend):
   * {
   *   activityId: Long,
   *   userId: Long,
   *   email: String,
   *   phone: String,
   *   channelType: String (email, sms, voice, social-media),
   *   activityType: String,
   *   messageId: Long,
   *   subject: String,
   *   contentSnippet: String,
   *   status: String,
   *   createdAt: LocalDateTime,
   *   sentAt: LocalDateTime,
   *   deliveredAt: LocalDateTime,
   *   senderId: String,
   *   ipAddress: String,
   *   userAgent: String,
   *   externalId: String,
   *   trackingId: String,
   *   properties: String (JSON)
   * }
   */

  /**
   * Write a message activity to RecentSimulations backend
   * @param {Object} messageData - The message data that was sent
   * @param {string} [messageData.messageId] - Unique message identifier
   * @param {string} [messageData.phone] - Phone number for SMS/voice activities
   * @param {string} [messageData.subject] - Message subject
   * @param {string} [messageData.content] - Message content
   * @param {string} [messageData.recipient] - Message recipient
   * @param {string} [messageData.communicationContext] - Activity type/context
   * @param {string} [messageData.sophisticationLevel] - Message sophistication level
   * @param {string} [messageData.status] - Message status (sent, delivered, failed)
   * @param {string} [messageData.senderId] - ID of the sender
   * @param {string} [messageData.ipAddress] - IP address of the sender
   * @param {string} [messageData.userAgent] - User agent string
   * @param {string} [messageData.externalId] - External system identifier
   * @param {string} [messageData.trackingId] - Tracking identifier
   * @param {Object} [messageData.properties] - Additional properties (will be JSON stringified)
   * @param {string} [messageData.timestamp] - Custom timestamp
   * @param {string} [messageData.sentAt] - When message was sent
   * @param {string} [messageData.deliveredAt] - When message was delivered
   * @param {string} channelType - The communication channel (email, sms, voice, social-media)
   */
  writeActivity: async (messageData, channelType) => {
    try {
      // Get current user from cookie
      const userUuid = getCookie("user");
      if (!userUuid) {
        console.warn(
          "[RecentSimulationsAPI] No user selected, skipping recent activity write",
        );
        return;
      }

      // Get user ID from UUID using cookie-based authentication
      const userResponse = await APIService.get(`/api/v1/base/users/by-uuid`);
      if (!userResponse || !userResponse.userId) {
        console.warn(
          "[RecentSimulationsAPI] Unable to retrieve user information for recent activity",
        );
        return;
      }
      const userId = userResponse.userId;

      // Get current timestamp for timing fields
      const currentTimestamp = new Date().toISOString();

      // Prepare properties object to store additional frontend data
      const properties = {
        recipient: getRecipient(messageData, channelType),
        sophisticationLevel: messageData.sophisticationLevel || "Basic",
        originalContent: getMessageContent(messageData),
        ...(messageData.properties || {}), // Include any additional properties passed in
      };

      // Prepare the activity data to match backend RecentActivityDTO structure
      const activityData = {
        userId: userId,
        email: String(
          userResponse.email ||
            userResponse.userEmail ||
            (typeof userResponse === "object" ? userResponse.email : null) ||
            "unknown@example.com",
        ),
        phone: messageData.phone || userResponse.phone || null,
        channelType: String(channelType.toLowerCase()),
        activityType: String(
          messageData.communicationContext ||
            messageData.activityType ||
            "simulation",
        ),
        messageId: (() => {
          // Handle different messageId types and convert to Long
          if (messageData.messageId) {
            if (typeof messageData.messageId === "number") {
              return messageData.messageId;
            }
            if (typeof messageData.messageId === "string") {
              const extracted = messageData.messageId.replace(/\D/g, "");
              return extracted ? parseInt(extracted) : Date.now();
            }
          }
          return Date.now(); // Fallback to current timestamp
        })(),
        subject: String(messageData.subject || getDefaultSubject(channelType)),
        contentSnippet: String(
          getMessageContent(messageData).substring(0, 500),
        ), // Limit content snippet
        status: String(messageData.status || "sent"),
        createdAt: String(currentTimestamp),
        sentAt: String(messageData.sentAt || currentTimestamp),
        deliveredAt: messageData.deliveredAt
          ? String(messageData.deliveredAt)
          : null,
        senderId: String(messageData.senderId || "system"),
        ipAddress: messageData.ipAddress ? String(messageData.ipAddress) : null,
        userAgent: messageData.userAgent
          ? String(messageData.userAgent)
          : typeof navigator !== "undefined" && navigator.userAgent
            ? String(navigator.userAgent)
            : null,
        externalId: messageData.externalId
          ? String(messageData.externalId)
          : null,
        trackingId: String(
          messageData.trackingId ||
            `track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        ),
        properties: JSON.stringify(properties),
      };

      console.log(
        `[RecentSimulationsAPI] Writing ${channelType} activity to recent activities:`,
        activityData,
      );

      // Debug: Log the types of key fields to help identify issues
      console.log("[RecentSimulationsAPI] Field types:", {
        userId: typeof activityData.userId,
        email: typeof activityData.email,
        properties: typeof activityData.properties,
        messageId: typeof activityData.messageId,
      });

      // Write to the recent activities API
      await APIService.post(
        "/api/v1/simulation/communication-simulations",
        activityData,
      );

      console.log(
        `[RecentSimulationsAPI] Successfully wrote ${channelType} activity to recent activities`,
      );
    } catch (error) {
      console.error(
        `[RecentSimulationsAPI] Error writing ${channelType} activity to recent activities:`,
        error,
      );
      throw error; // Re-throw so the caller can handle it
    }
  },
};

export default SimulationHeuristicsAPI;
